// Brand-declared auto discount catalog (#35626).
//
// Branch's auto discounts (Community Drive, paperless, employee, multi-policy/multiline, …) are
// hard-coded across staff-fe, account-fe, the Simple Quote PDF and the auto docs. A brand whose
// auto product is rated off a different plan (CFM, via ClarionDoor) offers a different set, so it
// declares that set in its brand config (`config.autoDiscounts`, brand-config-be AND
// brand-config-fe) as ids from this catalog. Every surface that shows auto discounts then renders
// exactly that list — and none of Branch's — resolved against the offer/policy by
// `resolveAutoDiscounts`. A brand without `autoDiscounts` keeps Branch's legacy discount handling.
//
// Lives in @ourbranch/lookups because it is the one package that the FE apps, both brand-config
// packages and every backend consumer (docs-renderer, offer-pdf-document, renewal comparison)
// already depend on, so the labels and applied-rules can't drift between surfaces.

export const AutoDiscountId = {
  MultiCar: 'multiCar',
  Homeowner: 'homeowner',
  PaidInFull: 'paidInFull',
  Transfer: 'transfer',
  Renewal: 'renewal',
  ClaimsFree: 'claimsFree',
  ViolationFree: 'violationFree'
} as const;

export type AutoDiscountId = (typeof AutoDiscountId)[keyof typeof AutoDiscountId];

type AutoDiscountDefinition = {
  label: string;
  description?: string;
  // ClarionDoor savings row (option.autoCoverages / segment coverages `type`) whose amount is the
  // discount's premium effect. Absent for discounts the rater applies from an input without
  // returning a savings row — those are applied from the same input instead.
  savingsCoverageType?: string;
};

export const autoDiscountDefinitions: Record<AutoDiscountId, AutoDiscountDefinition> = {
  // Applied as the CFM rater rates it: `num_vehicles = multiCarVehicleCount(...)` (#36778, #36891).
  // Trailers are a separate array and not counted.
  [AutoDiscountId.MultiCar]: {
    label: 'Multi Car',
    description: 'More than one car is insured and the cars have different principal drivers.'
  },
  [AutoDiscountId.Homeowner]: {
    label: 'Homeowner',
    description: 'The primary named insured owns their residence.',
    savingsCoverageType: 'homeowner_savings'
  },
  [AutoDiscountId.PaidInFull]: {
    label: 'Paid in Full',
    description: 'The policy premium is paid in full rather than in installments.',
    savingsCoverageType: 'paid_in_full_savings'
  },
  [AutoDiscountId.Transfer]: {
    label: 'Transfer',
    savingsCoverageType: 'transfer_savings'
  },
  [AutoDiscountId.Renewal]: {
    label: 'Renewal',
    savingsCoverageType: 'renewal_savings'
  },
  // Per-driver inputs (`claims_freeYN` / `violation_freeYN`) derived by CfmAutoPostDataRules.
  [AutoDiscountId.ClaimsFree]: {
    label: 'Claims Free',
    description: 'A rated driver has had no at-fault claims in the last 3 years.'
  },
  [AutoDiscountId.ViolationFree]: {
    label: 'Violation Free',
    description: 'A rated driver has had no chargeable violations.'
  }
};

// CFM auto (ClarionDoor PPA_VEHICLES_2), in display order.
export const CFM_AUTO_DISCOUNTS: AutoDiscountId[] = [
  AutoDiscountId.MultiCar,
  AutoDiscountId.Homeowner,
  AutoDiscountId.PaidInFull,
  AutoDiscountId.Transfer,
  AutoDiscountId.Renewal,
  AutoDiscountId.ClaimsFree,
  AutoDiscountId.ViolationFree
];

type AutoDiscountCoverage = { type?: string | null; amount?: number | null };
type AutoDiscountCar = { VIN?: string | null };
type AutoDiscountDriver = {
  excludeDriver?: boolean | null;
  assignedCar?: string | null;
  autoClaimsFree?: boolean | null;
  autoViolationFree?: boolean | null;
};

/**
 * The car count ClarionDoor's multi_car table (23528) reads. CD applies the multi-car factor at 2+
 * cars, but GO applies it only when the cars' principal drivers are two or more DIFFERENT drivers
 * (exact on all 233 multi-car GO autos). The Guidewire importer records each car's principal driver
 * as driver.assignedCar, so when assignments exist and fewer than two distinct cars have an assigned
 * rated driver, this reports 1 car. Otherwise it keeps the plain car count:
 * - no assignments at all (CFM doesn't run the car↔driver assignment for policies written in Branch);
 * - an assignment pointing at a car no longer on the policy. CFM has no assignment UI, so a car swap
 *   leaves it stale and the assignments no longer describe the policy (devlo, #36779).
 *
 * Shared by the CFM rater (num_vehicles feeds only multi_car in the CFM auto plans) and
 * resolveAutoDiscounts, so the Multi Car line every surface prints is the one that was rated. #36778 #36891
 */
export function multiCarVehicleCount(
  cars: readonly unknown[],
  ratedDrivers: readonly (AutoDiscountDriver | null | undefined)[]
): number {
  const vins = new Set(cars.map((car) => (car as AutoDiscountCar | null | undefined)?.VIN).filter(Boolean));
  const assignedVins = ratedDrivers.map((driver) => driver?.assignedCar).filter((vin): vin is string => !!vin);
  if (cars.length < 2 || !assignedVins.length || assignedVins.some((vin) => !vins.has(vin))) {
    return cars.length;
  }
  return new Set(assignedVins).size >= 2 ? cars.length : 1;
}

export type ResolvedAutoDiscount = {
  id: AutoDiscountId;
  label: string;
  description?: string;
  isApplied: boolean;
  // Summed savings-row amount; null for a discount that has no savings row.
  amount: number | null;
};

/**
 * Resolves a brand's auto discount list against an offer option or policy segment: whether each
 * discount applies and, when the rater returns a savings row for it, the summed amount across
 * every iteration (one row per rated vehicle).
 */
export function resolveAutoDiscounts({
  discountIds,
  cars,
  drivers,
  autoCoverages,
  importedDiscountIds
}: {
  discountIds: readonly AutoDiscountId[];
  cars?: readonly unknown[] | null;
  drivers?: readonly (AutoDiscountDriver | null | undefined)[] | null;
  autoCoverages?: readonly (AutoDiscountCoverage | null | undefined)[] | null;
  /**
   * The discounts the SOURCE system applied on a migrated term (`segment.auto.importedDiscounts`,
   * written by the importer from GO's per-risk Discount rows, #36344). They stand in for the
   * ClarionDoor savings rows only while the term has never been rated here: a rated segment always
   * carries its savings rows (at 0 when a discount does not apply), and those are authoritative — a
   * renewal copies `auto` from the imported term, so a stale imported flag must never outrank them.
   */
  importedDiscountIds?: readonly string[] | null;
}): ResolvedAutoDiscount[] {
  const ratedDrivers = (drivers ?? []).filter(
    (driver): driver is AutoDiscountDriver => Boolean(driver) && !driver?.excludeDriver
  );
  const coverages = autoCoverages ?? [];
  const hasSavingsRows = coverages.some((coverage) => String(coverage?.type ?? '').endsWith('_savings'));
  const imported = new Set(importedDiscountIds ?? []);

  return discountIds.map((id) => {
    const { label, description, savingsCoverageType } = autoDiscountDefinitions[id];

    if (savingsCoverageType) {
      if (!hasSavingsRows && imported.size > 0) {
        return { id, label, description, isApplied: imported.has(id), amount: null };
      }
      const amount = coverages
        .filter((coverage) => coverage?.type === savingsCoverageType)
        .reduce((total, coverage) => total + (Number(coverage?.amount) || 0), 0);
      return { id, label, description, isApplied: amount > 0, amount };
    }

    let isApplied = false;
    if (id === AutoDiscountId.MultiCar) {
      const multiCarRated = multiCarVehicleCount(cars ?? [], ratedDrivers) > 1;
      // An imported term (never rated here) shows what GO applied; there is no savings row to read. #36891
      isApplied = !hasSavingsRows && imported.size > 0 ? multiCarRated && imported.has(id) : multiCarRated;
    } else if (id === AutoDiscountId.ClaimsFree) {
      isApplied = ratedDrivers.some((driver) => driver.autoClaimsFree === true);
    } else if (id === AutoDiscountId.ViolationFree) {
      isApplied = ratedDrivers.some((driver) => driver.autoViolationFree === true);
    }
    return { id, label, description, isApplied, amount: null };
  });
}
